/**
 * Insurer Dialog Messages Hook
 *
 * Provides messages for insurer search dialogs based on clinic management type
 */

import { useAuth } from '@shared/providers/AuthContext';
import { useTenantInsurerDialogContact } from './useTenantInsurerDialogContact';

interface ClinicManagementInfo {
  siteType: string;
  phone: string;
  department: string;
}

interface DialogResponse {
  message: string;
  shouldClearInsurerNumber: boolean;
}

export function useInsurerDialogMessages() {
  const { clinic } = useAuth();
  const siteType = clinic?.site_type || 'medix';

  const tenantConfigQuery = useTenantInsurerDialogContact(siteType);

  const fallbackBySiteType: Record<string, Omit<ClinicManagementInfo, 'siteType'>> = {
    medix: {
      phone: '03-3255-1095',
      department: '審査部',
    },
    hitowa: {
      phone: '',
      department: 'システム管理者',
    },
  };

  /**
   * Get clinic management information for dialog messages
   * Determined by clinic configuration/site type
   */
  const getClinicManagementInfo = (): ClinicManagementInfo => {
    const fallback = fallbackBySiteType[siteType] ?? {
      phone: '',
      department: 'システム管理者',
    };

    const contactConfig = tenantConfigQuery.data?.data?.config_insurer_dialog_contact;
    const phone = (contactConfig?.phone ?? fallback.phone ?? '').trim();
    const department = (
      contactConfig?.department ??
      fallback.department ??
      'システム管理者'
    ).trim();

    return {
      siteType,
      phone,
      department,
    };
  };

  /**
   * Warning message for insurers with no payment history
   * Does not clear insurer number
   */
  const getWarningDialog1Message = (): DialogResponse => {
    const info = getClinicManagementInfo();
    const contactText = info.phone ? `${info.phone}　${info.department}` : info.department;
    const message = `この保険者は現在請求実績がありません。調査いたしますので、【${contactText}】までお問い合わせください。`;

    return {
      message,
      shouldClearInsurerNumber: true,
    };
  };

  /**
   * Warning message for insurer numbers not found in master data
   * Clears insurer number on confirmation
   */
  const getWarningDialog2Message = (): DialogResponse => {
    //   Medix / Upsystem → 【03-3255-1095 審査部】; hitowa → 【システム管理者】.
    // Get from getClinicManagementInfo() (config_insurer_dialog_contact theo tenant).
    const info = getClinicManagementInfo();
    const contactText = info.phone ? `${info.phone} ${info.department}` : info.department;
    const message = `【保険者番号】入力されたコードはマスタに存在しません。お手数ですが、【${contactText}】までお問い合わせください。`;

    return {
      message,
      shouldClearInsurerNumber: true,
    };
  };

  /**
   * Warning message for 医療助成 公費番号 not found in master data.
   * Mirrors getWarningDialog2Message but with the 医療助成 wording / cancel guidance.
   */
  const getMedicalAidWarningDialog2Message = (): DialogResponse => {
    const info = getClinicManagementInfo();
    const contactText = info.phone ? `${info.phone} ${info.department}` : info.department;
    const message = `【医療助成　公費番号】入力されたコードはマスタに存在しません。お手数ですが、一旦【${contactText}】までお問い合わせいただき登録確認後に入力頂くか、キャンセルして、次の患者データを先にご登録ください。`;

    return {
      message,
      shouldClearInsurerNumber: true,
    };
  };

  /**
   * Info message for multiple results (merger/reorganization warning)
   */
  const getMultipleResultsWarningMessage = (): DialogResponse => {
    return {
      message: '【保険者番号】合併、再編等により名称が変更になっている可能性があります。',
      shouldClearInsurerNumber: false,
    };
  };

  /**
   * Info message for empty insurer number input
   */
  const getEmptyInsurerNumberMessage = (): string => {
    return '保険者番号が入力されていません。';
  };

  return {
    getClinicManagementInfo,
    getWarningDialog1Message,
    getWarningDialog2Message,
    getMedicalAidWarningDialog2Message,
    getMultipleResultsWarningMessage,
    getEmptyInsurerNumberMessage,
  };
}
